import { canGrant, catalogGroups, toggleGroup, togglePermission } from "../../lib/permissions";
import { MicroLabel } from "../ui/Text";

/**
 * Every permission as a checkbox, grouped by area, with a toggle per group.
 *
 * **What the actor does not hold is disabled, not hidden.** The server refuses to
 * let anyone grant a permission they lack, so offering it would be a button that
 * always fails. Hiding it would be worse: an operator editing a role they may only
 * partly change would not see what else the role carries, and a box that is
 * already ticked stays ticked and visible — it is not theirs to take away either,
 * so it is locked in place.
 *
 * The group toggle touches only the boxes the actor can change, for the same
 * reason.
 */
export function PermissionPicker({
  legend,
  value,
  held,
  onChange,
  disabled = false,
}: {
  readonly legend: string;
  readonly value: readonly string[];
  /** What the signed-in principal holds — the ceiling on what they can grant. */
  readonly held: readonly string[];
  readonly onChange: (next: readonly string[]) => void;
  readonly disabled?: boolean;
}) {
  const groups = catalogGroups();
  const lockedCount = groups
    .flatMap((group) => group.permissions)
    .filter((info) => !canGrant(held, info.key)).length;

  return (
    <fieldset className="m-0 flex flex-col gap-5 border-0 p-0" disabled={disabled}>
      <legend className="pb-1">
        <MicroLabel>{legend}</MicroLabel>
      </legend>

      {lockedCount === 0 ? null : (
        <p className="m-0 max-w-prose text-micro tracking-normal normal-case text-ink-faint">
          {lockedCount} {lockedCount === 1 ? "permission is" : "permissions are"} locked: you can
          only grant what you hold yourself.
        </p>
      )}

      <div className="grid grid-cols-1 gap-x-8 gap-y-5 md:grid-cols-2 xl:grid-cols-3">
        {groups.map((group) => {
          const keys = group.permissions.map((info) => info.key);
          const grantable = keys.filter((key) => canGrant(held, key));
          const allOn = grantable.length > 0 && grantable.every((key) => value.includes(key));

          return (
            <div key={group.group} className="flex flex-col gap-2">
              <label className="flex items-center gap-2 border-b border-rule pb-1 text-data font-semibold text-ink">
                <input
                  type="checkbox"
                  checked={allOn}
                  disabled={grantable.length === 0}
                  onChange={() => onChange(toggleGroup(value, keys, held))}
                  className="size-4 accent-signal"
                  aria-label={`All ${group.group} permissions`}
                />
                {group.group}
              </label>

              {group.permissions.map((info) => {
                const locked = !canGrant(held, info.key);
                return (
                  <label
                    key={info.key}
                    className={`flex items-start gap-2 text-body ${locked ? "text-ink-faint" : "text-ink"}`}
                  >
                    <input
                      type="checkbox"
                      checked={value.includes(info.key)}
                      disabled={locked}
                      onChange={() => onChange(togglePermission(value, info.key))}
                      className="mt-1 size-4 accent-signal"
                    />
                    <span>
                      {info.label}
                      <span className="block text-micro tracking-normal normal-case text-ink-faint">
                        {locked ? "You don't hold this, so you can't grant it." : info.description}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          );
        })}
      </div>
    </fieldset>
  );
}
